import { Alert, Button, colors, PageTitle, ScreenLoading, Stack, Typography } from '@ui-components/neo.tax';
import React, { ReactElement, ReactNode, useState } from 'react';

import { GetFiscalYearQuery, ProviderType, TicketingProvider } from '@neo.tax/shared/graphql';
import {
  useCurrentUserScopeForCurrentCompany,
  useDisabledProviders,
  useGitHubAppInstallReturn,
  useProviderTokensAndSyncedProviders,
} from '@neo.tax/shared/hooks';

import {
  BulkEditExpenseProjectAllocationReportModal,
  type BulkEditUpload,
} from '../bulk-edit-expense-project-allocation-report-modal';
import { ColumnVisibilityPopover, type ColumnVisibilityState } from '../column-visibility-popover';
import { ConnectProvidersModal } from '../connect-account-modal';
import { type CreateColumns, ExpenseTable, type StringIdentifiable } from '../expense-table';
import { FilterPopover, type FilterState } from '../filter-popover';
import { KeywordSearch, type KeywordSearchState } from '../keyword-search';
import { type PageLayoutProps } from '../page-layout';
import { SortPopover, type SortingState } from '../sort-popover';
import { SelectAllBanner } from './expense-layout.styled';

interface ExpenseLayoutProps<
  Row extends StringIdentifiable,
  FilterColumnName extends string,
  SortColumnName extends string,
  VisibilityColumnName extends string,
> {
  pageTitle: string;
  pageSubtitle: string;
  /** The provider types to allow the user to connect to on this page. */
  connectableProviderTypes: Set<ProviderType>;
  /** The label for the provider connect button. */
  connectProviderLabel: string;
  bulkEditProviderType: ProviderType;

  searchKeywordState: KeywordSearchState;
  filterState: FilterState<FilterColumnName>;
  sortState: SortingState<SortColumnName>;
  columnVisibilityState: ColumnVisibilityState<VisibilityColumnName>;
  tableRows: Row[] | null;
  loadingRows: boolean;
  loadingFooter: boolean;
  isPiiRedacted: boolean;

  onLoadMoreItems: () => Promise<void>;

  ExpenseDetail?: ({ expense }: { expense: Row }) => ReactElement;

  createColumns: CreateColumns<Row>;
  alert?: ReactNode;
  /** The lock page layout component to use (e.g., RdLockPageLayout, SredLockPageLayout) */
  LockPageLayout: React.ComponentType<PageLayoutProps>;
  /** Optional additional props to pass to the lock page layout component */
  lockPageLayoutProps?: Record<string, any>;

  // Fiscal year data
  fiscalYear: GetFiscalYearQuery['getFiscalYear'] | null;
  fiscalYearId: string;
  isNowPastPeriodEndDate: boolean;

  // Optional toolbar actions (like "Set R&D %" button)
  toolbarActions?: ReactNode;

  // Optional row selection callbacks
  onRowSelectionChange?: (selectedRowIds: string[]) => void;

  // "Select all" across pages support
  totalCount?: number;
  isAllSelected?: boolean;
  onSelectAllChange?: (isAllSelected: boolean) => void;

  // Optional analytic IDs for tracking
  analyticIds?: {
    bulkEditInExcelBtnId?: string;
    connectProviderBtnId?: string;
  };

  // Bulk edit template download and upload
  bulkEditTemplate: {
    onDownload: () => Promise<void>;
    isDownloading: boolean;
  };
  bulkEditUpload: BulkEditUpload;
}

export const ExpenseLayout = <
  Row extends StringIdentifiable,
  FilterColumnName extends string,
  SortColumnName extends string,
  VisibilityColumnName extends string,
>({
  ExpenseDetail,
  LockPageLayout,
  alert,
  analyticIds,
  bulkEditProviderType,
  bulkEditTemplate,
  bulkEditUpload,
  columnVisibilityState,
  connectProviderLabel,
  connectableProviderTypes,
  createColumns,
  filterState,
  fiscalYear,
  fiscalYearId,
  isAllSelected,
  isNowPastPeriodEndDate,
  isPiiRedacted,
  loadingFooter,
  loadingRows,
  lockPageLayoutProps,
  onLoadMoreItems,
  onRowSelectionChange,
  onSelectAllChange,
  pageSubtitle,
  pageTitle,
  searchKeywordState,
  sortState,
  tableRows,
  toolbarActions,
  totalCount,
}: ExpenseLayoutProps<Row, FilterColumnName, SortColumnName, VisibilityColumnName>): ReactElement => {
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { syncedProviders } = useProviderTokensAndSyncedProviders({ fiscalYearId });
  const disabledProviders = useDisabledProviders({
    disableConditions: { accounting: 'sync', payroll: 'sync' },
    fiscalYearId,
  });

  // Detail row is the row that is currently being shown in right drawer
  const [detailRowId, setDetailRowId] = useState<string | undefined>();
  // Selected row ids are the row ids that are currently selected in the table.
  const [selectedRowIds, setSelectedRows] = useState<string[]>([]);

  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

  const detailExpense = tableRows?.find(({ id }) => id === detailRowId);

  // A GitHub App install started from this page returns the browser here (?connected_github_app=...),
  // so this surface has to reopen the slide-over and clear the param just like the get-started page.
  const {
    closeConnectModal: closeConnectProviderModal,
    installStatus: githubAppInstallReturn,
    isConnectModalOpen: isConnectProviderModalOpen,
    openConnectModal: openConnectProviderModal,
  } = useGitHubAppInstallReturn();

  const isPeriodCurrentAndPartialYearAlert = !isNowPastPeriodEndDate && (
    <Alert
      severity="info"
      title="You're viewing a partial year. This is only an estimate. Please note that changes to projects and allocations will happen automatically when future data is resynced"
    />
  );

  const handleSelectRow = (checked: boolean, row: Row): void => {
    if (!checked) {
      onSelectAllChange?.(false);
    }
    setSelectedRows((prev) => {
      const newSelection = checked ? [...prev, row.id] : prev.filter((id) => id !== row.id);
      onRowSelectionChange?.(newSelection);
      return newSelection;
    });
  };

  const handleSelectAllRows = (checked: boolean): void => {
    if (!tableRows) {
      setSelectedRows([]);
      onRowSelectionChange?.([]);
      return;
    }
    const newSelection = checked ? tableRows.map((row) => row.id) : [];
    setSelectedRows(newSelection);
    onRowSelectionChange?.(newSelection);
    if (!checked) {
      onSelectAllChange?.(false);
    }
  };

  const allVisibleSelected = tableRows != null && tableRows.length > 0 && selectedRowIds.length === tableRows.length;
  const hasMoreBeyondVisible = totalCount != null && tableRows != null && totalCount > tableRows.length;
  const showSelectAllBanner = allVisibleSelected && hasMoreBeyondVisible && !isAllSelected;
  const showAllSelectedBanner = !!isAllSelected && totalCount != null;

  const handleShowRowDetail = (item: Row): void => {
    setDetailRowId(item.id);
  };

  const alerts = [isPeriodCurrentAndPartialYearAlert, alert]
    .filter(Boolean)
    .map((alert, index) => React.cloneElement(alert as React.ReactElement, { key: `alert-${index}` }));

  return (
    <LockPageLayout
      title={
        <PageTitle>
          {pageTitle}
          {fiscalYear !== null && (
            <>
              {' | '}
              <Typography color={colors.blue[600]} fontSize="inherit" fontWeight="inherit">
                Tax Year {fiscalYear.year}
              </Typography>
            </>
          )}
        </PageTitle>
      }
      subtitle={pageSubtitle}
      alert={alerts}
      pageHeaderBorder
      {...lockPageLayoutProps}
      trailingContent={
        !limitAccessToFinancialData && (
          <>
            <Button
              size="base"
              variant="primary"
              rounded="regular"
              onClick={(): void => setIsBulkEditModalOpen(true)}
              id={analyticIds?.bulkEditInExcelBtnId}
            >
              Edit in Excel
            </Button>
            <Button
              size="base"
              variant="primary"
              rounded="regular"
              onClick={openConnectProviderModal}
              id={analyticIds?.connectProviderBtnId}
            >
              {connectProviderLabel}
            </Button>
          </>
        )
      }
      fullWidth
    >
      <Stack gap={5}>
        {isPiiRedacted ? <></> : <KeywordSearch {...searchKeywordState} />}
        <FilterPopover itemsDescription="expenses" {...filterState} />
        <SortPopover itemsDescription="expenses" {...sortState} />
        <ColumnVisibilityPopover {...columnVisibilityState} />

        {toolbarActions}
      </Stack>
      <Stack fullHeight direction="column">
        <SelectAllBanner isVisible={showSelectAllBanner || showAllSelectedBanner}>
          {showSelectAllBanner && (
            <Alert
              severity="warning"
              title={`Only the ${selectedRowIds.length} visible items are selected.`}
              actions={[
                {
                  label: `Select all ${totalCount} items`,
                  onClick: (): void => onSelectAllChange?.(true),
                },
              ]}
            />
          )}
          {showAllSelectedBanner && (
            <Alert
              severity="info"
              title={`All ${totalCount} items are selected.`}
              actions={[
                {
                  label: 'Clear selection',
                  onClick: (): void => {
                    onSelectAllChange?.(false);
                    setSelectedRows([]);
                    onRowSelectionChange?.([]);
                  },
                },
              ]}
            />
          )}
        </SelectAllBanner>
        <Stack fullHeight>
          {loadingRows && !tableRows ? (
            <ScreenLoading />
          ) : (
            <ExpenseTable
              data={tableRows || []}
              onClickRow={handleShowRowDetail}
              onSelectRow={handleSelectRow}
              onSelectAllRows={handleSelectAllRows}
              selectedRowIds={selectedRowIds}
              syncedProviders={syncedProviders}
              initialSelectedRowId={detailRowId}
              loadMoreItems={onLoadMoreItems}
              createColumns={createColumns}
              loadingRows={loadingRows}
              loadingFooter={loadingFooter}
            />
          )}
          {ExpenseDetail && detailExpense && <ExpenseDetail expense={detailExpense} />}
        </Stack>
      </Stack>
      <ConnectProvidersModal
        fiscalYearId={fiscalYearId}
        isOpen={isConnectProviderModalOpen}
        close={closeConnectProviderModal}
        showProvidersInTypes={connectableProviderTypes}
        initialProvider={
          githubAppInstallReturn
            ? { provider: TicketingProvider.GithubIssues, providerType: ProviderType.Ticketing }
            : null
        }
        {...disabledProviders}
      />
      <BulkEditExpenseProjectAllocationReportModal
        providerType={bulkEditProviderType}
        isOpen={isBulkEditModalOpen}
        onClose={(): void => setIsBulkEditModalOpen(false)}
        bulkEditTemplate={bulkEditTemplate}
        bulkEditUpload={bulkEditUpload}
      />
    </LockPageLayout>
  );
};
